import { LeadCard } from 'models/Lead';
import {
  AssignedPerson,
  BusyEntry,
  OperationBlockKey,
  OperationContractType,
  OperationTurno,
} from 'models/OperationRequest';

import { OPS_WEEK_DAYS, parseOpsWeekStartDate } from './operationCatalogs';

export type AssignmentSlots = Record<string, AssignedPerson[]>;

/** «Primer turno disponible» al soltar en la sala. */
export const BLOCK_PRIORITY: OperationBlockKey[] = ['full', 'am', 'pm'];

const DAY_KEYS = OPS_WEEK_DAYS.map((d) => d.key);
const MONTHS_SHORT = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

export type BlockerKind = 'dup' | 'campaign' | 'same-req' | 'full' | 'type';

export interface Blocker {
  kind: BlockerKind;
  /** Solo `type`: se puede forzar con justificación. */
  soft?: boolean;
  busy?: BusyEntry;
  otherKey?: string;
}

export type SkipReason = 'campaign' | 'here' | 'same-day' | 'full';

export interface StorePlan {
  added: { key: string; day: string; block: OperationBlockKey }[];
  skipped: { day: string; reason: SkipReason; busy?: BusyEntry }[];
  soft?: BusyEntry;
}

export interface RuleContext {
  slots: AssignmentSlots;
  busy: Record<string, BusyEntry[]>;
  type: OperationContractType;
  forced: Record<string, string>;
}

export const leadToPerson = (lead: LeadCard): AssignedPerson => ({
  leadId: lead.id,
  firstName: lead.firstName || '',
  lastName: lead.lastName || '',
  cityName: lead.cityName,
  regionName: lead.regionName,
  email: lead.email || null,
});

export const personName = (p: Pick<AssignedPerson, 'firstName' | 'lastName'>) =>
  `${p.firstName} ${p.lastName}`.trim();

export const shortName = (
  p: Pick<AssignedPerson, 'firstName' | 'lastName'>
) => {
  const first = (p.firstName || '').split(' ')[0];
  const initial = (p.lastName || '').trim()[0];
  return initial ? `${first} ${initial}.` : first;
};

export const initials = (p: Pick<AssignedPerson, 'firstName' | 'lastName'>) =>
  `${(p.firstName || '').trim()[0] || ''}${
    (p.lastName || '').trim()[0] || ''
  }`.toUpperCase() || '?';

export const dayOfKey = (key: string) => key.split(':')[1];

export const peopleOf = (slots: AssignmentSlots, key: string) =>
  slots[key] || [];

export const filledCount = (slots: AssignmentSlots) =>
  Object.values(slots).reduce((sum, list) => sum + list.length, 0);

export const totalCupos = (turnos: OperationTurno[]) =>
  turnos.reduce((sum, t) => sum + t.cupos, 0);

export const keysOfPerson = (slots: AssignmentSlots, leadId: string) =>
  Object.entries(slots)
    .filter(([, list]) => list.some((p) => p.leadId === leadId))
    .map(([key]) => key);

export const isAssigned = (slots: AssignmentSlots, leadId: string) =>
  keysOfPerson(slots, leadId).length > 0;

/** Turno en otra campaña de la semana con tipo distinto al requerimiento. */
export const typeConflict = (
  busy: Record<string, BusyEntry[]>,
  leadId: string,
  type: OperationContractType
) => (busy[leadId] || []).find((b) => b.type !== type) || null;

/** Por qué no se puede asignar — null si se puede. */
export const blockerFor = (
  ctx: RuleContext,
  turno: OperationTurno,
  leadId: string,
  ignoreKey?: string
): Blocker | null => {
  const inTurno = peopleOf(ctx.slots, turno.key);
  if (inTurno.some((p) => p.leadId === leadId)) return { kind: 'dup' };
  const busy = (ctx.busy[leadId] || []).find((b) => b.day === turno.day);
  if (busy) return { kind: 'campaign', busy };
  const otherKey = keysOfPerson(ctx.slots, leadId).find(
    (key) => key !== ignoreKey && dayOfKey(key) === turno.day
  );
  if (otherKey) return { kind: 'same-req', otherKey };
  if (inTurno.length >= turno.cupos) return { kind: 'full' };
  const conflict = typeConflict(ctx.busy, leadId, ctx.type);
  if (conflict && !ctx.forced[leadId]) {
    return { kind: 'type', soft: true, busy: conflict };
  }
  return null;
};

export const assignPerson = (
  slots: AssignmentSlots,
  key: string,
  person: AssignedPerson
): AssignmentSlots => ({
  ...slots,
  [key]: [...peopleOf(slots, key), person],
});

export const unassignPerson = (
  slots: AssignmentSlots,
  key: string,
  leadId: string
): AssignmentSlots => {
  const next = peopleOf(slots, key).filter((p) => p.leadId !== leadId);
  const copy = { ...slots };
  if (next.length) copy[key] = next;
  else delete copy[key];
  return copy;
};

export const movePerson = (
  slots: AssignmentSlots,
  fromKey: string,
  toKey: string,
  leadId: string
): AssignmentSlots => {
  const person = peopleOf(slots, fromKey).find((p) => p.leadId === leadId);
  if (!person || fromKey === toKey) return slots;
  return assignPerson(unassignPerson(slots, fromKey, leadId), toKey, person);
};

/** Soltar en la sala: todos sus días, FULL → AM → PM con cupo libre. */
export const planStore = (
  ctx: RuleContext,
  turnos: OperationTurno[],
  storeId: number,
  leadId: string,
  force = false
): StorePlan => {
  const plan: StorePlan = { added: [], skipped: [] };
  const conflict = typeConflict(ctx.busy, leadId, ctx.type);
  if (conflict && !ctx.forced[leadId] && !force) {
    return { ...plan, soft: conflict };
  }
  const storeTurnos = turnos.filter((t) => t.storeId === storeId);
  const days = [...new Set(storeTurnos.map((t) => t.day))].sort(
    (a, b) => DAY_KEYS.indexOf(a) - DAY_KEYS.indexOf(b)
  );
  const ownKeys = keysOfPerson(ctx.slots, leadId);

  days.forEach((day) => {
    const busy = (ctx.busy[leadId] || []).find((b) => b.day === day);
    if (busy) {
      plan.skipped.push({ day, reason: 'campaign', busy });
      return;
    }
    const own = ownKeys.find((key) => dayOfKey(key) === day);
    if (own) {
      plan.skipped.push({
        day,
        reason: own.startsWith(`${storeId}:`) ? 'here' : 'same-day',
      });
      return;
    }
    const pick = storeTurnos
      .filter((t) => t.day === day)
      .sort(
        (a, b) =>
          BLOCK_PRIORITY.indexOf(a.block) - BLOCK_PRIORITY.indexOf(b.block)
      )
      .find((t) => peopleOf(ctx.slots, t.key).length < t.cupos);
    if (!pick) {
      plan.skipped.push({ day, reason: 'full' });
      return;
    }
    plan.added.push({ key: pick.key, day, block: pick.block });
  });
  return plan;
};

export const applyStorePlan = (
  slots: AssignmentSlots,
  plan: StorePlan,
  person: AssignedPerson
) =>
  plan.added.reduce((acc, item) => assignPerson(acc, item.key, person), slots);

/** Descarta turnos que ya no existen en el plan (p. ej. tras editar). */
export const sanitizeSlots = (
  slots: AssignmentSlots,
  turnos: OperationTurno[]
): AssignmentSlots => {
  const keys = new Set(turnos.map((t) => t.key));
  return Object.fromEntries(
    Object.entries(slots).filter(([key, list]) => keys.has(key) && list.length)
  );
};

/** Solo conserva justificaciones de personas que siguen asignadas. */
export const cleanForced = (
  slots: AssignmentSlots,
  forced: Record<string, string>
) =>
  Object.fromEntries(
    Object.entries(forced).filter(([leadId]) => isAssigned(slots, leadId))
  );

export const sortTurnos = (turnos: OperationTurno[]) =>
  [...turnos].sort(
    (a, b) => BLOCK_PRIORITY.indexOf(a.block) - BLOCK_PRIORITY.indexOf(b.block)
  );

/** Días con al menos un turno, en orden lun → dom. */
export const activeDays = (turnos: OperationTurno[]) =>
  OPS_WEEK_DAYS.filter((d) => turnos.some((t) => t.day === d.key));

/** Fecha corta ("26 sep") de cada día según la semana del requerimiento. */
export const weekDayDates = (week: string): Record<string, string> => {
  const monday = parseOpsWeekStartDate(week);
  if (!monday) return {};
  return Object.fromEntries(
    DAY_KEYS.map((key, i) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      return [key, `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}`];
    })
  );
};
